import {
  memo,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useAppContext } from "../Context/AppContext";
import { clearTokens } from "../auth/authStorage";
import { useTranslation } from "react-i18next";
import type { IconType } from "react-icons";
import { FaLock, FaUnlock } from "react-icons/fa";
import {
  FiBriefcase,
  FiCalendar,
  FiEdit,
  FiHome,
  FiLogOut,
  FiMail,
  FiMessageSquare,
  FiRss,
  FiUser,
  FiUserPlus,
  FiUsers,
} from "react-icons/fi";
import {
  LuPanelLeftClose,
  LuPanelLeftOpen,
  LuPanelRightClose,
  LuPanelRightOpen,
} from "react-icons/lu";
import { isBusinessOrExecutiveMembership } from "../utils/membershipTier";
import { ADMIN_NAV_ITEMS, type AdminNavKey } from "../constants/adminNav";

const SIDEBAR_TRANSITION_MS = 500;
const SIDEBAR_OPEN_PX = 280;
const SIDEBAR_CLOSED_PX = 48;
const ICON_SLOT_PX = 48;
const SIDEBAR_PIN_STORAGE_KEY = "smbf.portal.sidebarPinned";
const EXPLORER_ENABLED_PATHS = new Set([
  "/event-booking",
  "/messages",
  "/connections",
  "/members-benefits-smbf",
]);

const LOCKED_MENU_TOOLTIP_DELAY_MS = 600;

/** Delayed lock hint — appears once near the pointer, does not track cursor movement. */
function LockedMenuCursorTooltip({
  tooltipId,
  menuName,
  children,
}: {
  tooltipId: string;
  menuName: string;
  children: ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === "rtl";
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const cursorRef = useRef({ x: 0, y: 0 });
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearShowTimer = () => {
    if (showTimerRef.current !== null) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
  };

  const hideTooltip = () => {
    clearShowTimer();
    setVisible(false);
  };

  useEffect(() => () => clearShowTimer(), []);

  const onMouseEnter = (e: ReactMouseEvent) => {
    cursorRef.current = { x: e.clientX, y: e.clientY };
    clearShowTimer();
    setVisible(false);
    showTimerRef.current = setTimeout(() => {
      setPos(cursorRef.current);
      setVisible(true);
      showTimerRef.current = null;
    }, LOCKED_MENU_TOOLTIP_DELAY_MS);
  };

  const onMouseMove = (e: ReactMouseEvent) => {
    // Keep latest pointer while waiting; freeze once shown.
    if (visible) return;
    cursorRef.current = { x: e.clientX, y: e.clientY };
  };

  return (
    <>
      <span
        className="d-block w-100"
        onMouseEnter={onMouseEnter}
        onMouseMove={onMouseMove}
        onMouseLeave={hideTooltip}
      >
        {children}
      </span>
      {visible &&
        createPortal(
          <div
            id={tooltipId}
            role="tooltip"
            className="popover position-fixed shadow-sm border-0 show"
            style={{
              left: pos.x,
              top: pos.y,
              transform: isRtl
                ? "translate(calc(-100% - 12px), 12px)"
                : "translate(12px, 12px)",
              zIndex: 1080,
              pointerEvents: "none",
              margin: 0,
              maxWidth: 240,
            }}
          >
            <div className="popover-body py-2 px-3 small">
              <div className="d-flex align-items-start gap-2">
                <FaLock
                  size={12}
                  className="text-primary flex-shrink-0 mt-1"
                  aria-hidden
                />
                <div>
                  <div className="fw-semibold text-dark lh-sm">
                    {t("layout.memberMenuLockedTitle", { name: menuName })}
                  </div>
                  <div className="fw-semibold text-dark mt-1 lh-sm">
                    {t("layout.memberMenuLockedHint")}
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

interface SidebarProps {
  isOpen: boolean;
  toggleSidebar: () => void;
  closeSidebarOnMobile?: () => void;
  /** Unpaid explorer: disable member nav except Events, Connections, Messages, and View memberships. */
  membershipLocked?: boolean;
}

function Sidebar({
  isOpen,
  toggleSidebar,
  closeSidebarOnMobile,
  membershipLocked = false,
}: SidebarProps) {
  const { t, i18n } = useTranslation();
  const { user, viewMode } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();
  const sidebarRef = useRef<HTMLElement>(null);
  const isRtl = i18n.dir() === "rtl";
  const isDesktop = useMediaQuery({ minWidth: 1024 });
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const [isPinned, setIsPinned] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }
    return window.localStorage.getItem(SIDEBAR_PIN_STORAGE_KEY) === "1";
  });

  const togglePinned = useCallback(() => {
    setIsPinned((prev) => {
      const next = !prev;
      window.localStorage.setItem(SIDEBAR_PIN_STORAGE_KEY, next ? "1" : "0");
      return next;
    });
  }, []);

  const handleLogout = useCallback(async () => {
    clearTokens();
    navigate("/", { replace: true });
    closeSidebarOnMobile?.();
  }, [navigate, closeSidebarOnMobile]);

  const handleNavigate = useCallback(
    (path: string) => {
      navigate(path);
      closeSidebarOnMobile?.();
    },
    [navigate, closeSidebarOnMobile],
  );

  const handleAdminNavigate = useCallback(
    (tab: AdminNavKey) => {
      navigate("/admin", { state: { adminTab: tab } });
      closeSidebarOnMobile?.();
    },
    [navigate, closeSidebarOnMobile],
  );

  const isActive = useCallback(
    (path: string) => {
      const current = location.pathname.replace(/\/+$/, "") || "/";
      const target = path.replace(/\/+$/, "") || "/";
      if (target === "/event-booking") {
        return (
          current === "/event-booking" ||
          current.startsWith("/event-booking/") ||
          current.startsWith("/event-questions") ||
          current.startsWith("/confirm-booking")
        );
      }
      if (target === "/members-benefits-smbf") {
        return (
          current === "/members-benefits-smbf" ||
          current.startsWith("/members-benefits-smbf/") ||
          current === "/business-membership" ||
          current === "/executive-membership" ||
          current === "/youth-membership" ||
          current === "/early-stage-membership"
        );
      }
      return current === target || current.startsWith(`${target}/`);
    },
    [location.pathname],
  );

  useEffect(() => {
    if (!isOpen || isPinned) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (sidebarRef.current && !sidebarRef.current.contains(target)) {
        toggleSidebar();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, isPinned, toggleSidebar]);

  const isAdmin = useMemo(
    () => user?.user?.isAdmin === 1,
    [user?.user?.isAdmin],
  );
  const showAdminMenu = isAdmin && viewMode === "admin";

  const showMuslimInvest = useMemo(
    () => isBusinessOrExecutiveMembership(user?.user?.membershipName),
    [user?.user?.membershipName],
  );

  const menuItems = useMemo(
    (): Array<{ path: string; label: string; Icon: IconType }> => [
      { path: "/updates", label: t("sidebar.updates"), Icon: FiRss },
      { path: "/dashboard", label: t("sidebar.dashboard"), Icon: FiHome },
      { path: "/event-booking", label: t("sidebar.events"), Icon: FiCalendar },
      ...(showMuslimInvest
        ? [
            {
              path: "/investing",
              label: t("sidebar.muslimInvest"),
              Icon: FiBriefcase,
            },
          ]
        : []),
      { path: "/partners", label: t("sidebar.partners"), Icon: FiUserPlus },
      { path: "/messages", label: t("sidebar.messages"), Icon: FiMessageSquare },
      {
        path: "/connections",
        label: t("sidebar.connections"),
        Icon: FiUsers,
      },
      { path: "/attendee-notes", label: t("sidebar.attendeeNotes"), Icon: FiEdit },
      { path: "/contact-us", label: t("sidebar.contactUs"), Icon: FiMail },
      { path: "/my-account", label: t("sidebar.myAccount"), Icon: FiUser },
    ],
    [t, showMuslimInvest],
  );

  const currentAdminTab = useMemo((): AdminNavKey => {
    if (location.pathname.startsWith("/admin/event")) {
      return "events";
    }
    const fromState = (location.state as { adminTab?: AdminNavKey } | null)
      ?.adminTab;
    if (fromState && ADMIN_NAV_ITEMS.some((item) => item.key === fromState)) {
      return fromState;
    }
    return "events";
  }, [location.pathname, location.state]);

  const isExplorerNavEnabled = useCallback(
    (path: string) =>
      !membershipLocked || EXPLORER_ENABLED_PATHS.has(path),
    [membershipLocked],
  );

  const lockedItemStyle: CSSProperties = {
    opacity: 0.4,
    cursor: "not-allowed",
    color: "#fff",
  };

  const linkClass = (active: boolean, hovered: boolean) =>
    [
      "nav-link",
      "text-white",
      "d-flex",
      "align-items-center",
      "justify-content-start",
      "border-0",
      "py-2",
      "pe-3",
      "ps-0",
      "rounded-0",
      "text-nowrap",
      "text-start",
      "gap-0",
      active || hovered ? "bg-project-transparent" : "bg-transparent",
    ]
      .filter(Boolean)
      .join(" ");

  const hoverBind = (key: string) => ({
    onMouseEnter: () => setHoveredKey(key),
    onMouseLeave: () => setHoveredKey(null),
  });

  const wrapDisabledTooltip = (
    disabled: boolean,
    tooltipId: string,
    menuName: string,
    node: ReactElement,
  ) => {
    if (!disabled) {
      return node;
    }
    return (
      <LockedMenuCursorTooltip tooltipId={tooltipId} menuName={menuName}>
        {node}
      </LockedMenuCursorTooltip>
    );
  };

  const linkStyle: CSSProperties = { width: SIDEBAR_OPEN_PX };

  const SidebarToggleIcon = isRtl
    ? isOpen
      ? LuPanelRightClose
      : LuPanelRightOpen
    : isOpen
      ? LuPanelLeftClose
      : LuPanelLeftOpen;

  const portalTitle = t("sidebar.portalTitle");

  /** Fade labels in/out with the rail; layout stays fixed so icons don't jump. */
  const labelStyle: CSSProperties = {
    opacity: isOpen ? 1 : 0,
    transition: `opacity ${SIDEBAR_TRANSITION_MS}ms ease`,
    whiteSpace: "nowrap",
  };

  return (
    <>
      {!isDesktop && (
        <div
          className="position-fixed start-0 w-100 bg-dark"
          style={{
            top: "calc(80px + 0.5rem)",
            zIndex: 1035,
            height: "calc(100vh - 80px - 0.5rem)",
            opacity: isOpen ? 0.45 : 0,
            transition: `opacity ${SIDEBAR_TRANSITION_MS}ms ease`,
            pointerEvents: isOpen && !isPinned ? "auto" : "none",
          }}
          onClick={isPinned ? undefined : toggleSidebar}
          aria-hidden={!isOpen}
        />
      )}
      <aside
        ref={sidebarRef}
        className={[
          "position-fixed",
          "bg-project-gradient",
          "text-white",
          "shadow",
          "overflow-hidden",
          isRtl ? "end-0" : "start-0",
        ]
          .filter(Boolean)
          .join(" ")}
        style={{
          top: "calc(80px + 0.5rem)",
          zIndex: 1040,
          height: "calc(100vh - 80px - 0.5rem)",
          width: isOpen ? SIDEBAR_OPEN_PX : SIDEBAR_CLOSED_PX,
          transition: `width ${SIDEBAR_TRANSITION_MS}ms ease`,
          ...(isRtl
            ? { borderTopLeftRadius: "var(--bs-border-radius)" }
            : { borderTopRightRadius: "var(--bs-border-radius)" }),
        }}
      >
        {/*
          Fixed inner width = open width. Outer aside clips on close so icons
          never reflow / jump — labels are revealed by the width animation.
        */}
        <div
          className="d-flex flex-column h-100"
          style={{ width: SIDEBAR_OPEN_PX, minWidth: SIDEBAR_OPEN_PX }}
        >
          <div className="sticky-top border-bottom border-white border-opacity-25 bg-project-gradient">
            <div className="position-relative d-flex align-items-center px-2 py-3">
              <div
                className="d-flex align-items-center gap-2 flex-grow-1 min-w-0 overflow-hidden pe-5"
                style={labelStyle}
                aria-hidden={!isOpen}
              >
                <img
                  src="/logoOld.png"
                  alt={t("nav.logoAlt")}
                  width={40}
                  height={40}
                  className="flex-shrink-0"
                  style={{ objectFit: "contain" }}
                />
                <div className="flex-grow-1 min-w-0 overflow-hidden">
                  <div
                    className="fw-semibold text-white lh-sm"
                    style={{ whiteSpace: "normal" }}
                  >
                    {portalTitle}
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="btn btn-outline-light btn-sm p-1 border border-white border-opacity-50 rounded d-inline-flex align-items-center justify-content-center position-absolute top-50 translate-middle-y"
                style={{
                  width: 32,
                  height: 32,
                  transition: `inset-inline-start ${SIDEBAR_TRANSITION_MS}ms ease`,
                  insetInlineStart: isOpen
                    ? `calc(100% - 40px)`
                    : `${(ICON_SLOT_PX - 32) / 2}px`,
                }}
                onClick={toggleSidebar}
                title={t("nav.toggleNavigation")}
                aria-label={t("nav.toggleNavigation")}
                aria-expanded={isOpen}
              >
                <SidebarToggleIcon size={18} aria-hidden />
              </button>
            </div>
          </div>

          <div className="position-relative flex-grow-1 overflow-hidden d-flex flex-column">
            <ul className="navbar-nav flex-column flex-grow-1 overflow-auto py-3 mb-0">
              {showAdminMenu
                ? ADMIN_NAV_ITEMS.map((item) => {
                    const Icon = item.Icon;
                    const label = t(item.labelKey);
                    const adminActive = !!(
                      isActive("/admin") && currentAdminTab === item.key
                    );
                    return (
                      <li key={item.key} className="nav-item">
                        <button
                          type="button"
                          className={linkClass(
                            adminActive,
                            hoveredKey === item.key,
                          )}
                          style={linkStyle}
                          aria-current={adminActive ? "page" : undefined}
                          title={label}
                          aria-label={label}
                          onClick={() => handleAdminNavigate(item.key)}
                          {...hoverBind(item.key)}
                        >
                          <span
                            className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
                            style={{
                              width: ICON_SLOT_PX,
                              minWidth: ICON_SLOT_PX,
                            }}
                          >
                            <Icon size={20} aria-hidden />
                          </span>
                          <span className="text-truncate" style={labelStyle}>
                            {label}
                          </span>
                        </button>
                      </li>
                    );
                  })
                : menuItems.map((item) => {
                    const Icon = item.Icon;
                    const navDisabled = !isExplorerNavEnabled(item.path);
                    return (
                      <li key={item.path} className="nav-item">
                        {wrapDisabledTooltip(
                          navDisabled,
                          `sidebar-locked-${item.path}`,
                          item.label,
                          <button
                            type="button"
                            className={linkClass(
                              !navDisabled && isActive(item.path),
                              !navDisabled && hoveredKey === item.path,
                            )}
                            style={{
                              ...linkStyle,
                              ...(navDisabled ? lockedItemStyle : undefined),
                            }}
                            disabled={navDisabled}
                            aria-disabled={navDisabled}
                            aria-current={
                              !navDisabled && isActive(item.path)
                                ? "page"
                                : undefined
                            }
                            aria-label={item.label}
                            onClick={() => {
                              if (navDisabled) return;
                              handleNavigate(item.path);
                            }}
                            {...(navDisabled ? {} : hoverBind(item.path))}
                          >
                            <span
                              className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
                              style={{
                                width: ICON_SLOT_PX,
                                minWidth: ICON_SLOT_PX,
                              }}
                            >
                              <Icon size={20} aria-hidden />
                            </span>
                            <span className="text-truncate" style={labelStyle}>
                              {item.label}
                            </span>
                          </button>,
                        )}
                      </li>
                    );
                  })}
            </ul>
            {membershipLocked && (
              <div className="d-flex flex-column">
                <button
                  type="button"
                  className={linkClass(
                    isActive("/register-user"),
                    hoveredKey === "memberships",
                  )}
                  style={linkStyle}
                  aria-current={
                    isActive("/register-user") ? "page" : undefined
                  }
                  title={t("layout.viewMemberships")}
                  aria-label={t("layout.viewMemberships")}
                  onClick={() => handleNavigate("/register-user")}
                  {...hoverBind("memberships")}
                >
                  <span
                    className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
                    style={{ width: ICON_SLOT_PX, minWidth: ICON_SLOT_PX }}
                  >
                    <FiUserPlus size={20} aria-hidden />
                  </span>
                  <span className="text-truncate" style={labelStyle}>
                    {t("layout.viewMemberships")}
                  </span>
                </button>
              </div>
            )}
          </div>

          <div className="mt-auto sticky-bottom border-top border-white border-opacity-25 py-2 position-relative z-1 bg-project-gradient">
            <div className="d-flex align-items-center w-100">
              <button
                type="button"
                className={[
                  linkClass(false, hoveredKey === "logout"),
                  isOpen ? "flex-grow-1 min-w-0" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                style={isOpen ? undefined : linkStyle}
                onClick={() => {
                  void handleLogout();
                }}
                title={t("sidebar.logout")}
                aria-label={t("sidebar.logout")}
                {...hoverBind("logout")}
              >
                <span
                  className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
                  style={{ width: ICON_SLOT_PX, minWidth: ICON_SLOT_PX }}
                >
                  <FiLogOut size={20} aria-hidden />
                </span>
                <span className="text-truncate" style={labelStyle}>
                  {t("sidebar.logout")}
                </span>
              </button>
              {isOpen && (
                <OverlayTrigger
                  placement={isRtl ? "left" : "right"}
                  overlay={
                    <Tooltip id="sidebar-lock-tooltip">
                      {isPinned
                        ? t("sidebar.unlockSidebar")
                        : t("sidebar.lockSidebar")}
                    </Tooltip>
                  }
                >
                  <button
                    type="button"
                    className="nav-link text-white d-inline-flex align-items-center justify-content-center border-0 py-2 px-3 rounded-0 bg-transparent flex-shrink-0"
                    onClick={togglePinned}
                    aria-label={
                      isPinned
                        ? t("sidebar.unlockSidebar")
                        : t("sidebar.lockSidebar")
                    }
                    aria-pressed={isPinned}
                  >
                    {isPinned ? (
                      <FaLock size={16} aria-hidden />
                    ) : (
                      <FaUnlock size={16} aria-hidden />
                    )}
                  </button>
                </OverlayTrigger>
              )}
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}

Sidebar.displayName = "Sidebar";

export default memo(Sidebar);
